import React, { useState, useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Menu, X, Share2, MessageSquareText, Facebook, Mail, Link2, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import Logo from "./Logo";
import Footer from "./Footer";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/blog", label: "Blog" },
  { to: "/astrology", label: "Astrology" },
  { to: "/contact", label: "Contact" },
];

function ShareModal({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  const items = [
    {
      icon: MessageSquareText,
      label: "Share via Text",
      href: `sms:?body=${encodeURIComponent(url)}`,
    },
    {
      icon: Mail,
      label: "Share via Email",
      href: `mailto:?subject=Holistic Intuitive Nutrition&body=${encodeURIComponent(url)}`,
    },
    {
      icon: Facebook,
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-purple">
            Share this page
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 mt-2">
          {items.map((it) => (
            <a
              key={it.label}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-md border border-neutral-200 text-neutral-700 hover:border-[var(--teal)] hover:text-[var(--teal-dark)] transition-colors"
            >
              <it.icon size={18} />
              <span className="text-sm font-medium tracking-wide-sm">{it.label}</span>
            </a>
          ))}
          <button
            onClick={copy}
            className="flex items-center gap-3 px-4 py-3 rounded-md border border-neutral-200 text-neutral-700 hover:border-[var(--teal)] hover:text-[var(--teal-dark)] transition-colors text-left"
          >
            {copied ? <Check size={18} className="text-[var(--teal-dark)]" /> : <Link2 size={18} />}
            <span className="text-sm font-medium tracking-wide-sm">
              {copied ? "Link copied!" : "Copy link"}
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          {/* Desktop */}
          <div className="hidden md:flex items-center justify-between py-4">
            <Logo compact />
            <nav className="flex items-center gap-9">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/book"
                className="btn-brand btn-teal"
              >
                Book a Call
              </Link>
            </nav>
          </div>
          {/* Mobile */}
          <div className="flex md:hidden items-center justify-between py-3">
            <Logo compact />
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              className="text-[var(--ink)] p-2"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-100 bg-white">
            <nav className="flex flex-col px-6 py-4 gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/book"
                className="btn-brand btn-teal mt-1 w-full"
              >
                Book a Call
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">{children}</main>

      {/* Site footer */}
      <Footer />

      {/* spacer so fixed bottom bar doesn't cover footer content */}
      <div className="h-14" aria-hidden="true" />

      {/* Fixed bottom bar (mirrors original site) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 flex h-14 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <Link
          to="/book"
          className="flex-1 bg-teal text-white flex items-center justify-center text-xs md:text-sm font-semibold tracking-wide-sm uppercase hover:bg-[var(--teal-dark)] transition-colors"
        >
          Get in touch with us!
        </Link>
        <button
          onClick={() => setShareOpen(true)}
          className="w-28 md:w-40 bg-[#1f2523] text-white flex items-center justify-center gap-2 text-xs md:text-sm font-semibold tracking-wide-sm uppercase hover:bg-black transition-colors"
        >
          <Share2 size={16} /> Share
        </button>
      </div>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}
